<template>
  <div>
    <v-row dense>
      <v-col cols="12" :md="hidePreview ? 12 : 6">
        <v-textarea
          v-model="bodyLocal"
          class="html-source"
          :label="label || $t('components.template.editor.htmlSource.source')"
          :hint="$t('components.template.editor.htmlSource.hint')"
          :height="height"
          persistent-hint
          outlined
          no-resize
          spellcheck="false"
        >
          <template #append>
            <template-editor-add-placeholder :partnerId="partnerId" @addPlaceholder="addPlaceholder" class="mt-n2" />
          </template>
        </v-textarea>
      </v-col>

      <v-col v-if="!hidePreview" cols="12" md="6">
        <div class="preview" :style="`height: ${height}px`">
          <div class="preview-label" :style="`background-color: ${$vuetify.theme.dark ? 'rgb(30, 30, 30)' : 'white'}`">
            {{ $t("components.template.editor.htmlSource.preview") }}
          </div>
          <iframe
            :srcdoc="previewHtml"
            :title="$t('components.template.editor.htmlSource.preview')"
            sandbox=""
            class="preview-frame"
          />
        </div>
        <div class="preview-hint">
          {{ $t("components.template.editor.htmlSource.previewHint") }}
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import TemplateEditorAddPlaceholder from "./TemplateEditorAddPlaceholder.vue";

/**
 * Raw html editor with a live preview of the rendered mail next to it.
 *
 * In contrast to the tiptap editor the content is stored exactly as it is typed, so a full html
 * document (including `<head>`, `<style>` and inline styles) survives unchanged.
 */
@Component({
  components: { TemplateEditorAddPlaceholder }
})
export default class TemplateEditorHtmlSource extends Vue {
  /**
   * Delay before the preview picks up the latest input, so the iframe is not re-rendered on every keystroke
   */
  static readonly PREVIEW_DEBOUNCE_MS = 300;

  @Prop({ default: "" })
  value?: string;

  @Prop()
  partnerId?: string;

  @Prop()
  label?: string;

  @Prop({ default: 400 })
  height!: number;

  @Prop({ default: false })
  hidePreview!: boolean;

  previewHtml = "";

  private previewTimeout?: number;

  get bodyLocal() {
    return this.value;
  }

  set bodyLocal(body: string | undefined) {
    this.$emit("input", body);
  }

  @Watch("value", { immediate: true })
  onValueChange() {
    window.clearTimeout(this.previewTimeout);
    this.previewTimeout = window.setTimeout(
      () => (this.previewHtml = this.value ?? ""),
      TemplateEditorHtmlSource.PREVIEW_DEBOUNCE_MS
    );
  }

  beforeDestroy() {
    window.clearTimeout(this.previewTimeout);
  }

  addPlaceholder(placeholder: string) {
    this.bodyLocal = (this.value ?? "") + placeholder;
  }
}
</script>

<style scoped>
.preview {
  position: relative;
  box-sizing: border-box;
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.38);
  padding: 1px;
}

.preview-label {
  position: absolute;
  top: -9px;
  left: 8px;
  padding: 0 4px;
  font-size: 12px;
  line-height: 1;
  color: rgb(103, 103, 103);
}

.theme--dark .preview-label {
  color: rgba(255, 255, 255, 0.7);
}

.preview-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 4px;
  /* mails are designed for a white background, independent of the portal theme */
  background-color: white;
}

.preview-hint {
  font-size: 12px;
  line-height: 12px;
  padding: 8px 12px 0 12px;
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .preview-hint {
  color: rgba(255, 255, 255, 0.7);
}
</style>

<style>
.html-source textarea {
  font-family: monospace;
  font-size: 0.8rem;
  line-height: 1.3;
}
</style>
